import { Component, OnInit, Inject, HostListener, Output, EventEmitter } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AsyncJobService } from '../../services/async-job.service';
import { AsyncJob } from '../../models/async-job.model';
import { ExcelExportService } from '../../services/excel-export.service';
import { ExcelImportService } from '../../services/excel-import.service';
import { ExportAsyncJobDialogBase } from '../async-job-base/export-async-job-base.dialog';
import { AsyncJobDialogBase, JOB_COMPLETED_MESSAGE } from '../async-job-base/async-job-base.dialog';
import { CurrentSiteService } from '../../services/current-site.service';
import { DataCollectionService } from '../../services/data-collection.service';
import { async, Observable } from 'rxjs';
import { ExportSiteMetrics } from '../../models/export-import/export-site-metrics.model';
import { ExcelImportMetrics } from '../../models/export-import/excel-import-metrics.model';
import { ExcelExportMetrics } from '../../models/export-import/excel-export-metrics.model';
import { IAsyncJobMonitor } from '../../interfaces/iasync-job-monitor.interface';
import { CurrentUserService } from '../../../security/current-user.service';

export enum ExportFormDataDialogAction {
    ExportSiteFormData = 'ExportSiteFormData',
    ExportFolderFormData = 'ExportFolderFormdata'
}

// Define a file upload progress class.
export class FileUploadProgress {
    // Progress information.
    public fileName: string;
    public progressValue: number = 0;
    public callerData: any;
}

// Export a progress hash class.
export interface IFileUploadProgressHash {
    [fileName: string]: { progress: Observable<FileUploadProgress> };
}

export class ExportFormDataDialogConfig {
    public action: ExportFormDataDialogAction;
    public objectId: number; // The id of the object being exported.
    public dataCollectionId: number;
    public folderId: number;
    public exportOnly: boolean = false;
    public startMessage: string = "Start Export ...";
    public cancelButton: string = "Cancel";
    public ImportStarted = false;
    public mode = "export";

    public constructor(action: ExportFormDataDialogAction, objectId: number, dataCollectionId: number) {
        this.action = action;
        this.objectId = objectId;
        this.dataCollectionId = dataCollectionId;
        this.folderId = objectId;
    }
}

@Component({
    selector: 'app-export-form-data',
    templateUrl: './export-form-data.dialog.html',
    styleUrls: [
        './export-form-data.dialog.scss',
        '../async-job-base/async-job-base.dialog.scss'
    ],
    standalone: false
})
export class ExportFormDataDialog extends AsyncJobDialogBase implements OnInit, IAsyncJobMonitor {
    // Properties.
    private siteFormNames: string[] = [];
    private selectedFormNames: string[] = []
    public Title: string = `Export ${this.FormSourceName} Form Data`;
    public startMessage: string = "Start Export ..."
    public importStarted: boolean = false;
    public cancelButton: string = "Cancel"
    public ExportDescription: string = `${this.FormSourceName} form data will export as an Excel spreadsheet.`;
    public ExportedDataUrl: string;
    public mode: string = "export";
    public validationMessages: string = "";
    public email: string = "";
    public shiftClick: boolean = false;
    private exportOnly: boolean = false;

    private hshProgressObservables: IFileUploadProgressHash = null;
    public importFile: File;
    private folderId: number = 0;

    private exportSiteMetrics: ExportSiteMetrics = null;
    private excelImportMetrics: ExcelImportMetrics = null;
    private excelExportMetrics: ExcelExportMetrics = null;

    // Constructor.
    public constructor(private dialogRef: MatDialogRef<ExportFormDataDialog>,
        @Inject(MAT_DIALOG_DATA) private config: ExportFormDataDialogConfig,
        private excelExportService: ExcelExportService,
        private excelImportService: ExcelImportService,
        protected injectedAsyncJobService: AsyncJobService,
        private currentSiteService: CurrentSiteService,
        private currentUserService: CurrentUserService,
        private dataCollectionService: DataCollectionService) {

        super(injectedAsyncJobService);
        dialogRef.disableClose = true;

        this.folderId = this.config.folderId;
    }
    @HostListener('window:keyup.esc') onKeyUp() {
        this.dialogRef.close();
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.folderId = this.config.folderId;
        this.exportOnly = this.config.exportOnly;
        this.email = this.currentUserService.user.emailAddress;
        this.dataCollectionService.getFormsForFolder(this.config.dataCollectionId, this.folderId, "names").then(formDefs => {
            if ((formDefs != null) && (formDefs.length > 0)) {
                this.siteFormNames = [];
                this.selectedFormNames = [];

                for (let index: number = 0; index < formDefs.length; index++) {
                    this.siteFormNames.push(formDefs[index].name);
                    this.selectedFormNames.push(formDefs[index].name);
                }
            }
        });
        return;
    }

    // Accessor methods are implemented in derived classes.
    public get FormNamesDivInlineStyles(): string {
        let height: number = Math.min(this.siteFormNames.length * 50 + 35, 225);
        let styles: string = `height: ${height}px;`;
        return styles;
    }
    public get FormNamesListInlineStyles(): string {
        let height: number = Math.min(this.siteFormNames.length * 50 + 10, 200);
        let styles: string = `height: ${height}px;`;
        return styles;
    }
    public get ExportOnly(): boolean {
        return this.exportOnly;
    }

    public get ExportDescriptionCssClass(): string {
        return (!this.JobInitiated ? 'active-p' : 'idle-p');
    }
    public get JobTimeElapsedCssClass(): string {
        return (this.JobInProgress ? 'active-execution-time-span' : 'idle-execution-time-span');
    }

    public get ExportStatus(): string {
        let status: string = null;

        if ((!this.jobInProgress) && (this.asyncJobInfo == null)) {
            status = 'Not started';
        } else if (this.jobInProgress) {
            status = 'Export in progress';
        } else if (!this.jobErrorOccurred) {
            if (this.ExportedDataUrl != null) {
                status = "Click 'Download' to save exported data";
            } else {
                status = JOB_COMPLETED_MESSAGE;//'Completed';
            }
        } else {
            status = 'An export error occurred';
        }

        return (status);
    }

    public get ExportStatusCssClass(): string {
        return (this.JobCompleted ? 'completed-execution-status-span' : 'incomplete-execution-status-span');
    }

    public get Progress(): IFileUploadProgressHash {
        return (this.hshProgressObservables);
    }

    // Implement methods called by my HTML code.
    public get ComponentClassName(): string {
        return 'ExportFormDataDialog';
    }
    public get ExportStructureName(): string {
        //return (this.config.action == ExportFormDataDialogAction.ExportSiteFormData ? 'Site Form Data' : 'Folder Form Data');
        return `${this.FormSourceName} Form Data`;
    }
    public get SiteFormNames(): string[] {
        return this.siteFormNames;
    }
    public get SelectedFormNames(): string[] {
        return this.selectedFormNames;
    }
    public set SelectedFormNames(value: string[]) {
        this.selectedFormNames = value;
    }
    public get NoFormNamesSelected(): boolean {
        return this.selectedFormNames.length == 0;
    }

    // Handle dialogue action(s).
    public formNamesSelectionChanged(): void {
        return;
    }

    public switchToExport(): void {
        this.mode = 'export';
        this.Title = `Export ${this.FormSourceName} Form Data`;;
        this.startMessage = "Start Export ...";
        this.cancelButton = "Cancel";
    }
    public switchToImport(): void {
        this.mode = 'import';
        this.Title = 'Import data from Excel';
        this.startMessage = "Start Import ...";
        this.ExportDescription = 'Form data will be updated from Excel data.';
        this.cancelButton = "Cancel";
    }

    public startFormDataExport(): void {
        if (this.config.action == ExportFormDataDialogAction.ExportSiteFormData) {
            this.excelExportService.exportSiteFormDataForSelectedForms(this.config.objectId, this.selectedFormNames).then(resultHash => {
                let asyncJob: AsyncJob = resultHash['asyncJob'];
                this.startMonitoringJob(asyncJob, this.jobUpdatedArrowFunction);
            });
        } else if (this.config.action == ExportFormDataDialogAction.ExportFolderFormData) {
            this.excelExportService.exportFolderFormDataForSelectedForms(this.config.objectId, this.selectedFormNames).then(asyncJob => {
                this.startMonitoringJob(asyncJob, this.jobUpdatedArrowFunctionExport, this.jobUpdatedArrowFunctionExport);
            });
        }
    }

    public startFormDataImport(event): void {
        //import Excel file
        this.reInit();
        this.folderId = this.config.folderId;
        this.startMessage = 'Import Started';
        this.importStarted = true;
        this.cancelButton = "Close";
        this.validationMessages = "";
        if (event.shiftKey) {
            this.shiftClick = true;
        }
        this.excelImportService.uploadExcelFile(
            this.currentSiteService.Site.id, this.folderId, this.importFile, this.email, this.shiftClick).then(asyncJob => {
                this.startMonitoringJob(asyncJob, this.jobCompletedArrowFunctionImport, this.jobUpdatedArrowFunctionImport);
        });
    }

    public get ExecutionSummaryText(): string {
        let summaryText: string = "";
        if (this.mode == 'import') {
            if (this.validationMessages) {
                return this.validationMessages;
            }
            summaryText = super.deriveExecutionSummaryText(this.excelImportMetrics);
        }
        else if (this.mode == 'export') {
            summaryText = super.deriveExecutionSummaryText(this.excelExportMetrics);
        }
        else {
            summaryText = super.deriveExecutionSummaryText(this.exportSiteMetrics);
        }

        return (summaryText);
    }

    public SplitPipe(theString): string {
        return theString.split("|");
    }

    // Implement interface IAsyncJobMonitor.
    public startMonitoringExistingJob(asyncJob: AsyncJob): void {
        this.startMonitoringJob(asyncJob, this.jobCompletedArrowFunction, this.jobCompletedArrowFunction);
    }

    public dismissDialogue(): void {
        this.dialogRef.close(this.ExportedDataUrl);
    }

    public cancelDialogue(): void {
        this.dialogRef.close(null);
    }

    public importFileSelected(event: Event) {
        const element = event.currentTarget as HTMLInputElement;
        let fileList: FileList | null = element.files;
        if (fileList) {
            this.importFile = fileList[0];
        }
        this.startMessage = "Start Import ...";
        this.ExportDescription = `${this.FormSourceName} form data will be updated from the selected Excel spreadsheet.`;
        this.mode = 'import';
    }

    // Private methods.

    private get FormSourceName(): string {
        return (this.config.action == ExportFormDataDialogAction.ExportSiteFormData ? 'Site' : 'Folder');
    }

    private static parseMetrics(jobMetricsJson: string): ExportSiteMetrics {
        let jobMetricsObject: Object = JSON.parse(jobMetricsJson);
        let metrics: ExportSiteMetrics = new ExportSiteMetrics(jobMetricsObject);
        return (metrics);
    }

    private static parseExportMetrics(jobMetricsJson: string): ExcelExportMetrics {
        let jobMetricsObject: Object = JSON.parse(jobMetricsJson);
        let metrics: ExcelExportMetrics = new ExcelExportMetrics(jobMetricsObject);
        return (metrics);
    }

    private static parseValidationMetrics(jobMetricsJson: string): ExcelImportMetrics {
        let jobMetricsObject: Object = JSON.parse(jobMetricsJson);
        let metrics: ExcelImportMetrics = new ExcelImportMetrics(jobMetricsObject);
        return (metrics);
    }

    private static parseImportMetrics(jobMetricsJson: string): ExcelImportMetrics {
        let jobMetricsObject: Object = JSON.parse(jobMetricsJson);
        let metrics: ExcelImportMetrics = new ExcelImportMetrics(jobMetricsObject);
        return (metrics);
    }

    private jobUpdatedArrowFunction = (asyncJob: AsyncJob): void => {
        this.ExportDescription = 'Export in progress.';
        if (asyncJob.stdOut) {
            this.exportSiteMetrics = ExportFormDataDialog.parseMetrics(asyncJob.stdOut);
        }

        if (asyncJob.status == AsyncJob.STATUS_COMPLETE) {
            let downloadFileName: string = null;
            if (this.config.action == ExportFormDataDialogAction.ExportSiteFormData) {
                downloadFileName = `site_${this.config.objectId}_form_data.xlsx`;
            } else if (this.config.action == ExportFormDataDialogAction.ExportFolderFormData) {
                downloadFileName = `folder_${this.config.objectId}_form_data.xlsx`;
            }
            this.ExportedDataUrl = this.excelExportService.getResultFileDownloadUrl(asyncJob, downloadFileName);
        }
        this.resetIdleTimer();
        return;
    }

    private jobUpdatedArrowFunctionExport = (asyncJob: AsyncJob): void => {
        this.ExportDescription = 'Export in progress.';
        if (asyncJob.stdOut) {
            this.excelExportMetrics = ExportFormDataDialog.parseExportMetrics(asyncJob.stdOut);
        }

        if (asyncJob.status == AsyncJob.STATUS_COMPLETE) {
            let downloadFileName: string = null;
            if (this.config.action == ExportFormDataDialogAction.ExportSiteFormData) {
                downloadFileName = `site_${this.config.objectId}_form_data.xlsx`;
            } else if (this.config.action == ExportFormDataDialogAction.ExportFolderFormData) {
                downloadFileName = `folder_${this.config.objectId}_form_data.xlsx`;
            }
            this.ExportDescription = 'Export complete, ready to download.';
            this.ExportedDataUrl = this.excelExportService.getResultFileDownloadUrl(asyncJob, downloadFileName);
        }

        this.resetIdleTimer();
        return;
    }

    private jobUpdatedArrowFunctionImport = (asyncJob: AsyncJob): void => {
        this.ExportDescription = 'Import in progress.';
        if (asyncJob.stdOut) {
            this.excelImportMetrics = ExportFormDataDialog.parseImportMetrics(asyncJob.stdOut);
        }
        this.resetIdleTimer();
        return;
    }

    private jobCompletedArrowFunction = (asyncJob: AsyncJob): void => {
        if ((asyncJob.stdOut != null) && (asyncJob.stdOut.trim() != '')) {
            this.exportSiteMetrics = ExportFormDataDialog.parseMetrics(asyncJob.stdOut);
        }
        return;
    }

    private jobCompletedArrowFunctionImport = (asyncJob: AsyncJob): void => {
        if (asyncJob.status == AsyncJob.STATUS_COMPLETE) {
            this.ExportDescription = 'Import complete.';
        }
        else if (asyncJob.status == AsyncJob.STATUS_COMPLETE_ERROR) {
            this.ExportDescription = 'Import failed.';
        }

        if (asyncJob.stdOut) {
            this.validationMessages = asyncJob.stdOut;
        }
        return;
    }
}
